import { create } from 'ipfs-http-client';
import { encryptJSON, decryptJSON } from './encryption';
import ipfsService from '../services/IPFSService';

// Configure IPFS client - using Infura IPFS node with environment variables (for uploads only)
const projectId = process.env.REACT_APP_INFURA_IPFS_PROJECT_ID || '2DBM8M1VMImQ5vl4IQxYQzI8uKg';
const projectSecret = process.env.REACT_APP_INFURA_IPFS_PROJECT_SECRET || 'f34bbedac4f01e7e79a9c99d04ba08cf';
const infuraAuth = 'Basic ' + Buffer.from(projectId + ':' + projectSecret).toString('base64');

// Client for uploads (uses Infura)
const client = create({
  host: 'infura.io',
  port: 5001,
  protocol: 'https',
  headers: {
    authorization: infuraAuth
  }
});

/**
 * Upload data to IPFS
 * @param {Object} data - The data to upload
 * @returns {Promise<string>} - IPFS URI with HTTPS gateway
 */
export const uploadToIPFS = async (data) => {
  try {
    const jsonData = JSON.stringify(data);
    const result = await client.add(jsonData);
    // Return the full HTTPS gateway URL using multi-gateway service
    return ipfsService.getPrimaryGatewayUrl(result.path);
  } catch (error) {
    console.error('Error uploading to IPFS:', error);
    throw error;
  }
};

/**
 * Encrypt and upload data to IPFS
 * @param {Object} data - The data to encrypt and upload
 * @param {string} encryptionKey - The key to use for encryption
 * @returns {Promise<string>} - IPFS URI with HTTPS gateway
 */
export const encryptAndUploadToIPFS = async (data, encryptionKey) => {
  try {
    // Encrypt the data
    const encryptedData = encryptJSON(data, encryptionKey);

    // Upload the encrypted data
    const result = await client.add(JSON.stringify(encryptedData));
    // Return the full HTTPS gateway URL using multi-gateway service
    return ipfsService.getPrimaryGatewayUrl(result.path);
  } catch (error) {
    console.error('Error encrypting and uploading to IPFS:', error);
    throw error;
  }
};

/**
 * Retrieve data from IPFS (now uses multi-gateway fallback)
 * @param {string} cid - The IPFS CID (Content Identifier)
 * @returns {Promise<Object>} - The retrieved data
 */
export const getFromIPFS = async (cid) => {
  try {
    // Use multi-gateway service for reliable fetching
    return await ipfsService.fetchJSON(cid);
  } catch (error) {
    console.error('Error retrieving from IPFS:', error);
    throw error;
  }
};

/**
 * Retrieve and decrypt data from IPFS
 * @param {string} cid - The IPFS CID (Content Identifier)
 * @param {string} encryptionKey - The key to use for decryption
 * @returns {Promise<Object>} - The decrypted data
 */
export const getAndDecryptFromIPFS = async (cid, encryptionKey) => {
  try {
    // Get the encrypted data from IPFS
    const encryptedData = await getFromIPFS(cid);
    
    // Decrypt the data
    return decryptJSON(encryptedData, encryptionKey);
  } catch (error) {
    console.error('Error retrieving and decrypting from IPFS:', error);
    throw error;
  }
};

/**
 * Generate IPFS gateway URL for a CID (now uses multi-gateway service)
 * @param {string} cid - The IPFS CID or URI (Content Identifier)
 * @returns {string} - The gateway URL
 */
export const getIPFSGatewayUrl = (cid) => {
  if (!cid) return '';

  // If it's already a full URL, return it
  if (cid.startsWith('http')) {
    return cid;
  }

  // Use IPFSService to generate gateway URL (uses primary gateway)
  return ipfsService.getPrimaryGatewayUrl(cid);
};

const ipfsUtils = {
  uploadToIPFS,
  encryptAndUploadToIPFS,
  getFromIPFS,
  getAndDecryptFromIPFS,
  getIPFSGatewayUrl
};

export default ipfsUtils;